import { useEffect, useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
  Checkbox,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { post, patch, fetchById } from "services/assets.service";
import Loading from "components/loading/Loading";
import { fetchAssets, refreshAsset } from "containers/assets/assets.slice";
import { fetchAssetDetails } from "containers/asset-view-container/asset-container.slice";
import DatePicker from "features/datepicker/date-picker";
import AssetsTypesSelect from "components/assets-types-select/assets-types-select";
import FileCategorySingleSelect from "components/file-category-single-select/file-category-single-select";
import FuelTypeSelect from "components/fuel-type-select/FuelTypeSelect";
import { isFullElectricVehicle } from "utils/fuelReportUtils";
import StickyFormFooter from "components/sticky-form-footer/sticky-form-footer";

function AssetsForm({ id }: { id?: string }) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams();
  const { filtersValue } = useAppSelector((state) => state.assetsReducer);
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [serial, setSerial] = useState("");
  const [purchase_date, setPurchaseDate] = useState("");
  const [fileCategory, setFileCategory] = useState<string[]>([]);

  const [isBroken, setIsBroken] = useState(false);

  // Fuel details state
  const [isFuelDetailsRequired, setIsFuelDetailsRequired] = useState(false);
  const [fuelConsumptionPerMonth, setFuelConsumptionPerMonth] = useState("");
  const [mileagePerMonth, setMileagePerMonth] = useState("");
  const [winterPeriodFuelRate, setWinterPeriodFuelRate] = useState("");
  const [summerPeriodFuelRate, setSummerPeriodFuelRate] = useState("");
  const [fuelType, setFuelType] = useState("");

  const [notes, setNotes] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = useCallback(
    (newChecked: boolean) => setIsBroken(newChecked),
    []
  );

  const handleFuelDetailsChange = useCallback(
    (newChecked: boolean) => setIsFuelDetailsRequired(newChecked),
    []
  );

  const fetchData = useCallback(
    async (id: string) => {
      setIsLoading(true);
      const data = await fetchById(companyId, id);
      const {
        name,
        notes,
        type,
        brand,
        model,
        serial,
        purchase_date,
        isBroken,
        fileCategory,
        isFuelDetailsRequired,
        fuelConsumptionPerMonth,
        mileagePerMonth,
        winterPeriodFuelRate,
        summerPeriodFuelRate,
        fuelType,
      } = data;

      setName(name);
      setNotes(notes);
      setType(type?.id || "");
      setBrand(brand);
      setModel(model);
      setSerial(serial);
      setPurchaseDate(purchase_date);
      setIsBroken(isBroken);
      fileCategory ? setFileCategory([fileCategory]) : setFileCategory([]);

      // Set fuel details
      setIsFuelDetailsRequired(isFuelDetailsRequired || false);
      setFuelConsumptionPerMonth(fuelConsumptionPerMonth);
      setMileagePerMonth(mileagePerMonth);
      setWinterPeriodFuelRate(winterPeriodFuelRate);
      setSummerPeriodFuelRate(summerPeriodFuelRate);
      setFuelType(fuelType || "");

      setIsLoading(false);
    },
    [companyId]
  );

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);

  if (!companyId) return null;

  const handleFormSubmit = async () => {
    const reform_category = fileCategory.length ? fileCategory[0] : null;
    const formData = {
      id,
      name,
      notes,
      type,
      brand,
      model,
      serial,
      purchase_date,
      isBroken,
      fileCategory: reform_category,
      isFuelDetailsRequired,
      winterPeriodFuelRate: isFuelDetailsRequired ? winterPeriodFuelRate : null,
      summerPeriodFuelRate: isFuelDetailsRequired ? summerPeriodFuelRate : null,
      fuelType: isFuelDetailsRequired ? fuelType : null,
    };
    const action = id ? patch : post;
    try {
      setIsLoading(true);
      await action(formData, companyId);
      if (id) {
        // Edit: refresh just this row, keeping loaded pages + scroll position.
        dispatch(refreshAsset(companyId, id));
        dispatch(fetchAssetDetails({ companyId, id }));
      } else {
        // Create: no existing row to patch, so reload the first page.
        dispatch(fetchAssets(companyId, filtersValue));
      }
      setSuccessMessage("ok");
      setIsLoading(false);
      setTimeout(() => {
        closeModal();
      }, 1000);
    } catch (e) {
      setErrMsg("error");
      setIsLoading(false);
    }
  };

  if (isLoading && !name) {
    return <Loading />;
  }

  const statusOptions = [
    { label: "-", value: "" },
    { label: t("asset_in_use"), value: "in use" },
    { label: t("asset_in_stock"), value: "in stock" },
    { label: t("asset_broken"), value: "broken" },
  ];

  const isFormValid = () => {
    return (
      name.trim() !== "" &&
      type.trim() !== "" &&
      fileCategory.length > 0 &&
      fileCategory[0] !== ""
    );
  };

  const handleNumericInputChange =
    (setter: (val: string) => void) => (value: string) => {
      const normalized = value.replace(",", ".");
      if (/^(\d+)?(\.)?(\d*)?$/.test(normalized)) {
        setter(normalized);
      }
    };

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormLayout>
        <div className="form-warnings">
          {errMsg && (
            <div className="">
              <Banner tone="critical">{t(errMsg)}</Banner>
            </div>
          )}
          {successMessage && (
            <div className="">
              <Banner tone="info">{t(successMessage)}</Banner>
            </div>
          )}
        </div>
        <TextField
          label={t(`asset_name`)}
          id="name"
          name="name"
          value={name}
          onChange={setName}
          autoComplete="off"
          requiredIndicator={true}
        />
        <AssetsTypesSelect isRequired={true} onChange={setType} value={type} />
        <TextField
          label={t(`asset_brand`)}
          id="brand"
          name="brand"
          value={brand}
          onChange={setBrand}
          autoComplete="off"
        />
        <TextField
          label={t(`asset_model`)}
          id="model"
          name="model"
          value={model}
          onChange={setModel}
          autoComplete="off"
        />
        <TextField
          label={t(`asset_serial`)}
          id="serial"
          name="serial"
          value={serial}
          onChange={setSerial}
          autoComplete="off"
        />
        <DatePicker
          title={t("asset_purchase_date")}
          onChange={setPurchaseDate}
          selected={purchase_date}
          disableDatesAfter=""
        />
        <FileCategorySingleSelect
          onChange={setFileCategory}
          value={fileCategory}
          isRequired={true}
        />
        <TextField
          label={t(`notes`)}
          id="notes"
          name="notes"
          value={notes}
          onChange={setNotes}
          autoComplete="off"
          multiline={4}
        />
        <Checkbox
          label={t("asset_broken")}
          checked={isBroken}
          onChange={handleChange}
        />

        {/* Fuel Details Section */}
        <Checkbox
          label={t("is_fuel_details_required")}
          checked={isFuelDetailsRequired}
          onChange={handleFuelDetailsChange}
        />

        {isFuelDetailsRequired && (
          <>
            <FuelTypeSelect
              value={fuelType}
              onChange={setFuelType}
            />
            <TextField
              label={
                isFullElectricVehicle(fuelType)
                  ? t("winter_period_electric_rate")
                  : t("winter_period_fuel_rate")
              }
              id="winterPeriod"
              name="winterPeriod"
              value={winterPeriodFuelRate}
              onChange={handleNumericInputChange(setWinterPeriodFuelRate)}
              autoComplete="off"
              type="text"
            />
            <TextField
              label={
                isFullElectricVehicle(fuelType)
                  ? t("summer_period_electric_rate")
                  : t("summer_period_fuel_rate")
              }
              id="summerPeriodFuelRate"
              name="summerPeriodFuelRate"
              value={summerPeriodFuelRate}
              onChange={handleNumericInputChange(setSummerPeriodFuelRate)}
              autoComplete="off"
              type="text"
            />
          </>
        )}
      </FormLayout>
      <StickyFormFooter>
        <Button
          variant="primary"
          onClick={() => {
            if (isFormValid()) {
              handleFormSubmit();
            } else {
              setErrMsg("Please fill all required fields.");
            }
          }}
          loading={isLoading}
        >
          {t("save")}
        </Button>
      </StickyFormFooter>
    </Form>
  );
}

export default AssetsForm;
